// src/products/dataroom/pages/PermissionsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Shield, Eye, Download, Printer, ChevronRight, Building2, Users, UserCheck, FolderOpen, File, Save, X } from 'lucide-react';
import { supabase } from '../../../config';
import RoleService from '../../../services/roleService';

const PermissionsPage = ({ userRole, onMessage }) => {
  const canManage = RoleService.hasAdminAccess(userRole);

  const [step, setStep] = useState(1); // 1 = Dealroom, 2 = Party, 3 = Team, 4 = Permissions Matrix
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Step 1: Dealroom selection
  const [datarooms, setDatarooms] = useState([]);
  const [selectedDealroomId, setSelectedDealroomId] = useState(null);

  // Step 2: Party selection
  const [parties, setParties] = useState([]);
  const [selectedPartyId, setSelectedPartyId] = useState(null);

  // Step 3: Team selection
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);

  // Step 4: Files and permissions
  const [files, setFiles] = useState([]);
  const [permissions, setPermissions] = useState({}); // { fileId: permissionType }

  const permissionTypes = [
    { id: 'none', name: 'No Access', icon: X, color: 'gray' },
    { id: 'view', name: 'View', icon: Eye, color: 'blue' },
    { id: 'print', name: 'Print+View', icon: Printer, color: 'purple' },
    { id: 'download', name: 'Download+View', icon: Download, color: 'green' }
  ];

  // Fetch all dealrooms
  const fetchDealrooms = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('dataroom_projects')
        .select('id, project_name, description')
        .order('project_name');

      if (error) throw error;
      setDatarooms(data || []);
    } catch (error) {
      console.error('Error fetching dealrooms:', error);
      onMessage && onMessage('Failed to load dealrooms', 'error');
    } finally {
      setLoading(false);
    }
  }, [onMessage]);

  // Fetch parties for selected dealroom
  const fetchParties = useCallback(async (dealroomId) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('dataroom_parties')
        .select('id, name, description, code')
        .eq('project_id', dealroomId)
        .order('name');

      if (error) throw error;
      setParties(data || []);
    } catch (error) {
      console.error('Error fetching parties:', error);
      onMessage && onMessage('Failed to load parties', 'error');
    } finally {
      setLoading(false);
    }
  }, [onMessage]);

  // Get permission status for a team
  const getTeamPermissionStatus = useCallback(async (dealroomId, teamId) => {
    try {
      // Fetch all files in the dealroom
      const { data: filesData, error: filesError } = await supabase
        .from('dataroom_items')
        .select('id')
        .eq('project_id', dealroomId);

      if (filesError || !filesData || filesData.length === 0) {
        return 'No Files';
      }

      const fileIds = filesData.map(f => f.id);

      // Fetch permissions for this team
      const { data: permsData, error: permsError } = await supabase
        .from('dataroom_file_permissions')
        .select('permission_type')
        .eq('team_id', teamId)
        .in('dataroom_item_id', fileIds);

      if (permsError) throw permsError;

      const totalFiles = fileIds.length;
      const filesWithPerms = permsData?.length || 0;
      const filesWithoutPerms = totalFiles - filesWithPerms;

      if (filesWithPerms === 0) {
        return 'All No Access';
      }

      // Count permission types
      const permCounts = {};
      permsData.forEach(perm => {
        permCounts[perm.permission_type] = (permCounts[perm.permission_type] || 0) + 1;
      });

      // Permission type name mapping
      const permNameMap = {
        'view': 'View',
        'print': 'Print+View',
        'download': 'Download+View',
        'none': 'No Access'
      };

      // Check if all files have the same permission
      const allSame = Object.keys(permCounts).length === 1 && filesWithoutPerms === 0;
      
      if (allSame) {
        const permType = Object.keys(permCounts)[0];
        const permName = permNameMap[permType] || permType;
        return `ALL ${permName}`;
      }

      // Mixed permissions
      if (filesWithoutPerms > 0) {
        return 'Mixed (Some No Access)';
      }

      return 'Mixed';
    } catch (error) {
      console.error('Error getting team permission status:', error);
      return 'Unknown';
    }
  }, []);

  // Fetch teams for selected party
  const fetchTeams = useCallback(async (partyId) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('dataroom_teams')
        .select(`
          id,
          name,
          description,
          dataroom_user_teams(count)
        `)
        .eq('party_id', partyId)
        .order('name');

      if (error) throw error;
      
      // Fetch permission status for each team
      const teamsWithStatus = await Promise.all(
        (data || []).map(async (team) => {
          if (selectedDealroomId) {
            const status = await getTeamPermissionStatus(selectedDealroomId, team.id);
            return { ...team, permissionStatus: status };
          }
          return { ...team, permissionStatus: null };
        })
      );

      setTeams(teamsWithStatus);
    } catch (error) {
      console.error('Error fetching teams:', error);
      onMessage && onMessage('Failed to load teams', 'error');
    } finally {
      setLoading(false);
    }
  }, [onMessage, selectedDealroomId, getTeamPermissionStatus]);

  // Fetch files and permissions for selected team
  const fetchFilesAndPermissions = useCallback(async (dealroomId, teamId) => {
    try {
      setLoading(true);

      // Fetch all files/folders in the dealroom
      const { data: filesData, error: filesError } = await supabase
        .from('dataroom_items')
        .select('id, name, is_folder, parent_id')
        .eq('project_id', dealroomId)
        .order('is_folder', { ascending: false })
        .order('name');

      if (filesError) throw filesError;

      // Fetch existing permissions for this team
      const { data: permsData, error: permsError } = await supabase
        .from('dataroom_file_permissions')
        .select('dataroom_item_id, permission_type')
        .eq('team_id', teamId)
        .in('dataroom_item_id', filesData.map(f => f.id));

      if (permsError) throw permsError;

      // Build permissions object: { fileId: permissionType }
      const permsObj = {};
      (permsData || []).forEach(perm => {
        permsObj[perm.dataroom_item_id] = perm.permission_type;
      });

      setFiles(filesData || []);
      setPermissions(permsObj);
    } catch (error) {
      console.error('Error fetching files and permissions:', error);
      onMessage && onMessage('Failed to load files and permissions', 'error');
    } finally {
      setLoading(false);
    }
  }, [onMessage]);

  // Load dealrooms on mount
  useEffect(() => {
    fetchDealrooms();
  }, [fetchDealrooms]);

  // Load parties when dealroom is selected
  useEffect(() => {
    if (selectedDealroomId && step >= 2) {
      fetchParties(selectedDealroomId);
    }
  }, [selectedDealroomId, step, fetchParties]);

  // Load teams when party is selected
  useEffect(() => {
    if (selectedPartyId && step >= 3) {
      fetchTeams(selectedPartyId);
    }
  }, [selectedPartyId, step, fetchTeams]);

  // Load files and permissions when team is selected
  useEffect(() => {
    if (selectedDealroomId && selectedTeamId && step === 4) {
      fetchFilesAndPermissions(selectedDealroomId, selectedTeamId);
    }
  }, [selectedDealroomId, selectedTeamId, step, fetchFilesAndPermissions]);

  // Handle step navigation
  const handleNext = () => {
    if (step === 1 && !selectedDealroomId) {
      onMessage && onMessage('Please select a Dealroom', 'error');
      return;
    }
    if (step === 2 && !selectedPartyId) {
      onMessage && onMessage('Please select a Party', 'error');
      return;
    }
    if (step === 3 && !selectedTeamId) {
      onMessage && onMessage('Please select a Team', 'error');
      return;
    }
    setStep(prev => Math.min(prev + 1, 4));
  };

  const handleBack = () => {
    if (step === 2) {
      setSelectedPartyId(null);
      setSelectedTeamId(null);
      setTeams([]);
    } else if (step === 3) {
      setSelectedTeamId(null);
    } else if (step === 4) {
      setFiles([]);
      setPermissions({});
    }
    setStep(prev => Math.max(prev - 1, 1));
  };

  // Get permission for a file
  const getFilePermission = (fileId) => {
    return permissions[fileId] || 'none';
  };

  // Set permission for a file
  const setFilePermission = (fileId, permissionType) => {
    setPermissions(prev => ({
      ...prev,
      [fileId]: permissionType === 'none' ? undefined : permissionType
    }));
  };

  // Build file tree
  const buildTree = (items, parentId = null) => {
    return items
      .filter(item => item.parent_id === parentId)
      .map(item => ({
        ...item,
        children: buildTree(items, item.id)
      }));
  };

  // Save permissions
  const handleSave = async () => {
    if (!selectedTeamId || !selectedDealroomId) {
      onMessage && onMessage('Missing required selections', 'error');
      return;
    }

    try {
      setSaving(true);

      // Get all file IDs
      const fileIds = files.map(f => f.id);

      // Delete existing permissions for this team
      await supabase
        .from('dataroom_file_permissions')
        .delete()
        .eq('team_id', selectedTeamId)
        .in('dataroom_item_id', fileIds);

      // Insert new permissions
      const permissionsToInsert = [];
      Object.keys(permissions).forEach(fileId => {
        const permType = permissions[fileId];
        if (permType && permType !== 'none') {
          permissionsToInsert.push({
            team_id: selectedTeamId,
            dataroom_item_id: parseInt(fileId),
            permission_type: permType
          });
        }
      });

      if (permissionsToInsert.length > 0) {
        const { error } = await supabase
          .from('dataroom_file_permissions')
          .insert(permissionsToInsert);

        if (error) throw error;
      }

      onMessage && onMessage('Permissions saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving permissions:', error);
      onMessage && onMessage('Failed to save permissions: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Render file tree recursively
  const renderFileTree = (items, level = 0) => {
    return items.map(item => (
      <React.Fragment key={item.id}>
        <tr className="hover:bg-gray-50 border-b border-gray-100">
          <td className="px-4 py-3" style={{ paddingLeft: `${level * 24 + 16}px` }}>
            <div className="flex items-center space-x-2">
              {item.is_folder ? (
                <FolderOpen className="h-4 w-4 text-blue-500" />
              ) : (
                <File className="h-4 w-4 text-gray-500" />
              )}
              <span className="text-sm font-medium text-gray-900">{item.name}</span>
            </div>
          </td>
          <td className="px-4 py-3">
            <select
              value={getFilePermission(item.id)}
              onChange={(e) => setFilePermission(item.id, e.target.value)}
              disabled={!canManage || saving}
              className={`text-sm px-3 py-1.5 rounded border w-full ${
                getFilePermission(item.id) === 'download' ? 'bg-green-50 border-green-300 text-green-700' :
                getFilePermission(item.id) === 'print' ? 'bg-purple-50 border-purple-300 text-purple-700' :
                getFilePermission(item.id) === 'view' ? 'bg-blue-50 border-blue-300 text-blue-700' :
                'bg-gray-50 border-gray-300 text-gray-600'
              } ${!canManage || saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {permissionTypes.map(perm => (
                <option key={perm.id} value={perm.id}>
                  {perm.name}
                </option>
              ))}
            </select>
          </td>
        </tr>
        {item.children && item.children.length > 0 && renderFileTree(item.children, level + 1)}
      </React.Fragment>
    ));
  };

  const selectedDealroom = datarooms.find(d => d.id === selectedDealroomId);
  const selectedParty = parties.find(p => p.id === selectedPartyId);
  const selectedTeam = teams.find(t => t.id === selectedTeamId);
  const fileTree = buildTree(files);

  if (!canManage) {
    return (
      <div className="space-y-6 min-h-screen pb-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <Shield className="h-6 w-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-medium text-yellow-800 mb-2">Access Restricted</h3>
              <p className="text-yellow-700">
                Only Creators and Support users can manage permissions. Please contact your administrator if you need access.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-h-screen pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Settings className="h-8 w-8 mr-3" />
            Permissions Management
          </h1>
          <p className="text-gray-600 mt-1">
            Set file and folder permissions for teams
          </p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          {[
            { num: 1, label: 'Select Dealroom', icon: Building2 },
            { num: 2, label: 'Select Party', icon: Users },
            { num: 3, label: 'Select Team', icon: UserCheck },
            { num: 4, label: 'Set Permissions', icon: Shield }
          ].map((stepInfo, index) => {
            const Icon = stepInfo.icon;
            const isActive = step === stepInfo.num;
            const isCompleted = step > stepInfo.num;
            const isClickable = step > stepInfo.num;

            return (
              <React.Fragment key={stepInfo.num}>
                <button
                  onClick={() => isClickable && setStep(stepInfo.num)}
                  disabled={!isClickable}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700 font-semibold'
                      : isCompleted
                      ? 'bg-green-50 text-green-700 cursor-pointer hover:bg-green-100'
                      : 'bg-gray-50 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-sm font-medium">{stepInfo.label}</span>
                </button>
                {index < 3 && (
                  <ChevronRight className="h-5 w-5 text-gray-400 mx-2" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {!loading && (
          <>
            {/* Step 1: Select Dealroom */}
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Dealroom</h2>
                {datarooms.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No dealrooms available</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {datarooms.map(dealroom => (
                      <button
                        key={dealroom.id}
                        onClick={() => setSelectedDealroomId(dealroom.id)}
                        className={`p-4 border-2 rounded-lg text-left transition-all ${
                          selectedDealroomId === dealroom.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Building2 className={`h-6 w-6 ${
                            selectedDealroomId === dealroom.id ? 'text-blue-600' : 'text-gray-400'
                          }`} />
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{dealroom.project_name}</h3>
                            {dealroom.description && (
                              <p className="text-sm text-gray-600 mt-1">{dealroom.description}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Select Party */}
            {step === 2 && selectedDealroomId && (
              <div className="space-y-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
                  <span>Dealroom:</span>
                  <button
                    onClick={() => setStep(1)}
                    className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {selectedDealroom?.project_name}
                  </button>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Party</h2>
                {parties.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No parties found in this dealroom</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {parties.map(party => (
                      <button
                        key={party.id}
                        onClick={() => setSelectedPartyId(party.id)}
                        className={`p-4 border-2 rounded-lg text-left transition-all ${
                          selectedPartyId === party.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Users className={`h-6 w-6 ${
                            selectedPartyId === party.id ? 'text-blue-600' : 'text-gray-400'
                          }`} />
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{party.name}</h3>
                            {party.code && (
                              <p className="text-xs text-gray-500 mt-1">Code: {party.code}</p>
                            )}
                            {party.description && (
                              <p className="text-sm text-gray-600 mt-1">{party.description}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Select Team */}
            {step === 3 && selectedPartyId && (
              <div className="space-y-4">
                <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center space-x-2">
                    <span>Dealroom:</span>
                    <button
                      onClick={() => {
                        setStep(1);
                        setSelectedPartyId(null);
                        setSelectedTeamId(null);
                        setTeams([]);
                      }}
                      className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {selectedDealroom?.project_name}
                    </button>
                  </div>
                  <ChevronRight className="h-4 w-4" />
                  <div className="flex items-center space-x-2">
                    <span>Party:</span>
                    <button
                      onClick={() => setStep(2)}
                      className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {selectedParty?.name}
                    </button>
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Team</h2>
                {teams.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <UserCheck className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No teams found in this party</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teams.map(team => {
                      const getStatusColor = (status) => {
                        if (!status) return 'text-gray-500';
                        if (status.includes('ALL View')) return 'text-blue-600 bg-blue-50';
                        if (status.includes('ALL Print+View')) return 'text-purple-600 bg-purple-50';
                        if (status.includes('ALL Download+View')) return 'text-green-600 bg-green-50';
                        if (status.includes('All No Access')) return 'text-gray-600 bg-gray-50';
                        if (status.includes('Mixed')) return 'text-orange-600 bg-orange-50';
                        return 'text-gray-500 bg-gray-50';
                      };

                      return (
                        <button
                          key={team.id}
                          onClick={() => setSelectedTeamId(team.id)}
                          className={`p-4 border-2 rounded-lg text-left transition-all ${
                            selectedTeamId === team.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <UserCheck className={`h-6 w-6 ${
                              selectedTeamId === team.id ? 'text-blue-600' : 'text-gray-400'
                            }`} />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-gray-900">{team.name}</h3>
                                {team.permissionStatus && (
                                  <span className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(team.permissionStatus)}`}>
                                    {team.permissionStatus}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {team.dataroom_user_teams?.[0]?.count || 0} members
                              </p>
                              {team.description && (
                                <p className="text-sm text-gray-600 mt-1">{team.description}</p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Set Permissions */}
            {step === 4 && selectedTeamId && (
              <div className="space-y-4">
                <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center space-x-2">
                    <span>Dealroom:</span>
                    <button
                      onClick={() => {
                        setStep(1);
                        setSelectedPartyId(null);
                        setSelectedTeamId(null);
                        setTeams([]);
                        setFiles([]);
                        setPermissions({});
                      }}
                      className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {selectedDealroom?.project_name}
                    </button>
                  </div>
                  <ChevronRight className="h-4 w-4" />
                  <div className="flex items-center space-x-2">
                    <span>Party:</span>
                    <button
                      onClick={() => {
                        setStep(2);
                        setSelectedTeamId(null);
                        setFiles([]);
                        setPermissions({});
                      }}
                      className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {selectedParty?.name}
                    </button>
                  </div>
                  <ChevronRight className="h-4 w-4" />
                  <div className="flex items-center space-x-2">
                    <span>Team:</span>
                    <button
                      onClick={() => {
                        setStep(3);
                        setFiles([]);
                        setPermissions({});
                      }}
                      className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {selectedTeam?.name}
                    </button>
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Set File & Folder Permissions</h2>
                
                {files.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <File className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No files or folders in this dealroom</p>
                  </div>
                ) : (
                  <>
                    {/* Bulk Permission Setter */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Shield className="h-5 w-5 text-blue-600" />
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Set Same Permission for All Files/Folders
                            </label>
                            <p className="text-xs text-gray-600">
                              Apply the selected permission to all {files.length} items at once
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <select
                            id="bulkPermission"
                            onChange={(e) => {
                              const bulkPermission = e.target.value;
                              if (bulkPermission && bulkPermission !== '') {
                                // Apply to all files
                                const newPermissions = {};
                                files.forEach(file => {
                                  newPermissions[file.id] = bulkPermission === 'none' ? undefined : bulkPermission;
                                });
                                setPermissions(newPermissions);
                                onMessage && onMessage(`Applied ${permissionTypes.find(p => p.id === bulkPermission)?.name || bulkPermission} to all ${files.length} items`, 'success');
                                // Reset dropdown
                                e.target.value = '';
                              }
                            }}
                            disabled={!canManage || saving}
                            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            defaultValue=""
                          >
                            <option value="">Select Permission...</option>
                            {permissionTypes.map(perm => (
                              <option key={perm.id} value={perm.id}>
                                {perm.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">File/Folder</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Permission</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fileTree.length > 0 ? (
                            renderFileTree(fileTree)
                          ) : (
                            <tr>
                              <td colSpan={2} className="text-center py-12 text-gray-500">
                                No files or folders
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        <div className="flex items-center space-x-3">
          {step === 4 && (
            <button
              onClick={handleSave}
              disabled={saving || !canManage}
              className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Permissions'}
            </button>
          )}
          {step < 4 && (
            <button
              onClick={handleNext}
              disabled={loading}
              className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </button>
          )}
        </div>
      </div>

      {/* Permission Types Reference */}
      {step === 4 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-800 mb-4">Permission Types</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {permissionTypes.map((permission) => {
              const Icon = permission.icon;
              return (
                <div key={permission.id} className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg bg-${permission.color}-100`}>
                    <Icon className={`h-5 w-5 text-${permission.color}-600`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{permission.name}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(PermissionsPage);
